import React, { useState, useRef, useMemo, useEffect } from 'react'
import { useFormik } from "formik"
import * as Yup from "yup"
import { useNavigate } from 'react-router-dom'

// component
import {
  CButton,
  CCard,
  CCardBody,
  CCol,
  CContainer,
  CFormInput,
  CInputGroup,
  CInputGroupText,
  CRow,
  CSpinner
} from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import { cilUser } from '@coreui/icons'

// components
import FormPassword from '../../../components/form/general-input-form/FormPassword'
import FormCaptcha from '../../../components/form/general-input-form/FormCaptcha'

// helpers
import Axios from '../../../helpers/axios'
import useToastMessage from 'src/helpers/toast-message'

// Regex minimal password: min 8 karakter, ada huruf besar, huruf kecil, dan angka.
// Diletakkan di luar komponen supaya tidak dibuat ulang tiap render (dinamis & mudah diubah).
const PASSWORD_MIN_LENGTH = 8
const PASSWORD_RULE_REGEX = {
  lowercase: /[a-z]/,
  uppercase: /[A-Z]/,
  number: /[0-9]/,
}

const registerSchema = Yup.object().shape({
  first_name: Yup.string().required("Please enter your first name."),
  last_name: Yup.string().required("Please enter your last name."),
  email: Yup.string()
    .email("Format email invalid.")
    .required("Please enter your email address."),
  password: Yup.string()
    .required("Please enter your password.")
    .min(PASSWORD_MIN_LENGTH, `Min ${PASSWORD_MIN_LENGTH} characters.`)
    .matches(PASSWORD_RULE_REGEX.lowercase, "Password must contain a lowercase letter.")
    .matches(PASSWORD_RULE_REGEX.uppercase, "Password must contain an uppercase letter.")
    .matches(PASSWORD_RULE_REGEX.number, "Password must contain a number."),
  confirm_password: Yup.string()
    .required("Please confirm your password.")
    .oneOf([Yup.ref('password')], "Password confirmation does not match the password."),
  captcha: Yup.string()
    .required("Please enter the captcha code."),
})

// Field yang harus valid dulu sebelum captcha ditampilkan.
const BASE_FIELD_KEYS = ['first_name', 'last_name', 'email', 'password', 'confirm_password']
const baseFieldsSchema = registerSchema.pick(BASE_FIELD_KEYS)

const Register = () => {
  const navigate = useNavigate()
  const { showError, showSuccess } = useToastMessage();

  const [isLoading, setIsLoading] = useState(false)
  const captchaRef = useRef(null)

  const formik = useFormik({
    initialValues: {
      first_name: '',
      last_name: '',
      email: '',
      password: '',
      confirm_password: '',
      captcha: '',
    },
    validationSchema: registerSchema,
    validateOnBlur: true,
    validateOnChange: false,
    onSubmit: async (values, { setSubmitting, setFieldValue }) => {
      const isCaptchaValid = captchaRef.current?.validate(values.captcha)

      if (!isCaptchaValid) {
        showError('Captcha code is invalid. Please try again.')
        captchaRef.current?.refresh()
        setFieldValue('captcha', '')
        setSubmitting(false)
        return
      }

      handleSave(values)
      setSubmitting(false)
    },
  });

  const isBaseFieldsValid = useMemo(
    () => baseFieldsSchema.isValidSync({
      first_name: formik.values.first_name,
      last_name: formik.values.last_name,
      email: formik.values.email,
      password: formik.values.password,
      confirm_password: formik.values.confirm_password,
    }),
    [
      formik.values.first_name,
      formik.values.last_name,
      formik.values.email,
      formik.values.password,
      formik.values.confirm_password,
    ]
  )

  useEffect(() => {
    if (!isBaseFieldsValid && formik.values.captcha) {
      formik.setFieldValue('captcha', '')
    }
  }, [isBaseFieldsValid])

  const handleInputValue = (values) => {
    const payload = {
      email: values.email,
      password: values.password,
      profile: {
        first_name: values.first_name,
        last_name: values.last_name
      },
      role_id: 1
    }

    return payload
  }

  const handlePost = (payload) => {
    setIsLoading(true)
    Axios({
      method: "post",
      url: '/api/sso/v1/admin/create',
      data: payload
    })
      .then(({ data }) => {
        if (data) {
          showSuccess('You have successfully registered, please check your mailbox to validate your account')
          Axios({
            method: "post",
            url: `/api/tracker/v1/email/send/register-account`,
            data: {
              user: {
                id: data?.id,
                email: data?.email
              }
            }
          })
            .then(() => {
              // success sending email verify user
            })
            .catch((err) => {
              console.error(err)
            })

          setTimeout(() => {
            navigate('/login')
          }, 2500)
        }
      })
      .catch((err) => {
        const errMsg = err?.response?.data?.message
        if (errMsg === 'user-not-verified') {
          showError('Your account is already. Please verify your email!')
        } else if (errMsg === 'user-not-active') {
          showError('Your account is waiting for approval')
        } else if (errMsg === 'email-already-exists' || errMsg === 'user-already-exists') {
          showError('Email sudah terdaftar, silakan gunakan email lain.')
        } else if (errMsg === 'invalid-password' || errMsg === 'wrong-password') {
          // Cek ulang saat password ditolak/salah oleh backend
          showError('Password not valid. Please ensure your password meets the required criteria.')
        } else {
          // fallback: sebelumnya error selain 2 kondisi di atas tidak menampilkan apa-apa ke user
          showError(errMsg || 'An error occurred during registration, please try again.')
        }

        // captcha selalu di-refresh setiap kali register gagal, mencegah reuse kode lama
        captchaRef.current?.refresh()
        formik.setFieldValue('captcha', '')
      })
      .finally(() => setIsLoading(false))
  }

  const handleSave = (values) => {
    const inputPayload = handleInputValue(values)
    handlePost(inputPayload)
  }

  return (
    <div className="bg-light min-vh-100 d-flex flex-row align-items-center">
      <CContainer>
        <CRow className="justify-content-center">
          <CCol md={9} lg={7} xl={6}>
            <CCard className="mx-4">
              <CCardBody className="p-4">
                <h1>Register</h1>
                <p className="text-medium-emphasis">Create your account</p>
                <CInputGroup className="mb-3">
                  <CInputGroupText>
                    <CIcon icon={cilUser} />
                  </CInputGroupText>
                  <CFormInput
                    id='first_name'
                    name='first_name'
                    placeholder="First Name"
                    value={formik.values.first_name}
                    onChange={(e) => formik.handleChange(e)}
                    onBlur={formik.handleBlur}
                    invalid={!!formik.errors.first_name}
                    feedbackInvalid={formik.errors.first_name}
                    autoComplete='off'
                  />
                </CInputGroup>
                <CInputGroup className="mb-3">
                  <CInputGroupText>
                    <CIcon icon={cilUser} />
                  </CInputGroupText>
                  <CFormInput
                    id='last_name'
                    name='last_name'
                    placeholder="Last Name"
                    value={formik.values.last_name}
                    onChange={(e) => formik.handleChange(e)}
                    onBlur={formik.handleBlur}
                    invalid={!!formik.errors.last_name}
                    feedbackInvalid={formik.errors.last_name}
                    autoComplete='off'
                  />
                </CInputGroup>
                <CInputGroup className="mb-3">
                  <CInputGroupText>@</CInputGroupText>
                  <CFormInput
                    type='email'
                    id='email'
                    name='email'
                    placeholder="Email Address"
                    value={formik.values.email}
                    onChange={(e) => formik.handleChange(e)}
                    onBlur={formik.handleBlur}
                    invalid={!!formik.errors.email}
                    feedbackInvalid={formik.errors.email}
                    autoComplete='off'
                  />
                </CInputGroup>

                {/* Password: sekarang pakai FormPassword (show/hide + validasi min 8 karakter) */}
                <FormPassword
                  id='password'
                  name='password'
                  placeholder="Password"
                  value={formik.values.password}
                  handleTextInput={formik.handleChange}
                  handleBlur={formik.handleBlur}
                  invalid={!!formik.errors.password}
                  feedbackInvalid={formik.errors.password}
                  className='mb-3'
                />

                {/* Confirm Password: field baru untuk mengecek ulang password yang salah ketik */}
                <FormPassword
                  id='confirm_password'
                  name='confirm_password'
                  placeholder="Confirm Password"
                  value={formik.values.confirm_password}
                  handleTextInput={formik.handleChange}
                  handleBlur={formik.handleBlur}
                  invalid={!!formik.errors.confirm_password}
                  feedbackInvalid={formik.errors.confirm_password}
                  className='mb-3'
                />

                {/* Captcha: baru muncul kalau field di atas sudah VALID (bukan cuma terisi) */}
                {isBaseFieldsValid && (
                  <FormCaptcha
                    ref={captchaRef}
                    id='captcha'
                    name='captcha'
                    colSize={12}
                    className='mb-3'
                    placeholder='Input captcha code'
                    value={formik.values.captcha}
                    handleTextInput={formik.handleChange}
                    handleBlur={formik.handleBlur}
                    invalid={!!formik.errors.captcha}
                    feedbackInvalid={formik.errors.captcha}
                  />
                )}

                <div className="d-grid gap-2 d-md-flex">
                  <CButton
                    color="dark"
                    onClick={() => formik.handleSubmit()}
                    disabled={isLoading}
                  >
                    {isLoading && (
                      <CSpinner
                        className='me-2'
                        component="span"
                        color="light"
                        size="sm"
                        aria-hidden="true"
                      />
                    )}
                    Create Account
                  </CButton>
                  <CButton
                    color="dark"
                    onClick={() => navigate('/login')}
                    disabled={isLoading}
                  >
                    Login
                  </CButton>
                </div>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      </CContainer>
    </div>
  )
}

export default Register